import React from 'react';
import { Chip } from '@mui/material';
import { Text, Theme, i18n, tokens } from '@simplybenefits/sb-ui-library';
import { numberWithCommas } from '../../services/helpers.js';
import Tooltip from '@mui/material/Tooltip';
import { Info as InfoIcon } from '@mui/icons-material';
import UsageBlock from './UsageBlock.js';

/**
 * @param usage
 * @param coverage
 * @param fillColor
 * @param waivedEHC
 * @returns {JSX.Element}
 * @constructor
 */
const ParamedicalsUsageBlock = ({ usage, coverage, fillColor, waivedEHC }) => {
    const isCombined = usage.isCombinedMaximum;

    /**
     *
     * @returns
     */
    const calculateCombinedParamedicalsTotals = () => {
        let total = 0;
        let mentalWellnessTotal = 0;
        let functionalTreatmentTotal = 0;

        // eslint-disable-next-line array-callback-return
        usage.used.map((item) => {
            if (usage.isMentalWellnessIncluded) {
                if (item.type === 'Social Worker' || item.type === 'Psychologist' || item.type === 'Psychotherapist') {
                    mentalWellnessTotal += item.used;
                    return null; // do not add mental wellness / functional treatments to the combined max total if they are grouped, as they have their own maximums
                }
            }

            if (usage.isFunctionalTreatmentIncluded) {
                if (item.type === 'Osteopath' || item.type === 'Chiropodist' || item.type === 'Podiatrist') {
                    functionalTreatmentTotal += item.used;
                    return null;
                }
            }

            total += item.used;
            return null;
        });

        mentalWellnessTotal = (usage.mentalWellnessCombinedMax - mentalWellnessTotal);
        functionalTreatmentTotal = (usage.functionalTreatmentCombinedMax - functionalTreatmentTotal);
        total = (usage.combinedMax - total);

        if (mentalWellnessTotal < 0) {
            mentalWellnessTotal = 0;
        }

        if (functionalTreatmentTotal < 0) {
            functionalTreatmentTotal = 0;
        }

        if (total < 0) {
            total = 0;
        }

        return {
            paramedical: numberWithCommas((total).toFixed(2)),
            mentalWellness: numberWithCommas((mentalWellnessTotal).toFixed(2)),
            functionalTreatment: numberWithCommas((functionalTreatmentTotal).toFixed(2))
        };
    };

    return (
        <React.Fragment>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <div>
                    <Text
                        medium
                        bold
                        customStyle={{
                            flex: 1,
                            display: 'inline',
                            margin: '0 0 10px 0'
                        }}
                    >
                        { i18n.t(tokens.titles.paramedicals) }
                    </Text>

                    {
                        waivedEHC &&
                            <Chip
                                size="small"
                                label="Waived"
                                color="primary"
                                variant="outlined"
                                style={{
                                    margin: "5px 10px",
                                    borderRadius: 30,
                                    borderWidth: 2,
                                    borderColor: Theme.colors.lightGrey,
                                    fontFamily: Theme.fonts.type.heading,
                                    fontSize: 14,
                                    color: '#666'
                                }}
                            />
                    }
                </div>

                {
                    isCombined &&
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <Tooltip
                                    title={
                                        usage.isFamilyCombinedMaximum ?
                                            i18n.t(tokens.labels.coverage.spendUpToAmountOnParamedicalServicesFamilyMaximum, { amount: calculateCombinedParamedicalsTotals().paramedical })
                                            :
                                            i18n.t(tokens.labels.coverage.spendUpToAmountOnParamedicalServices, { amount: calculateCombinedParamedicalsTotals().paramedical })
                                    }
                                    arrow
                                    placement="top"
                                >
                                    <InfoIcon aria-label="info" style={{ fontSize: 15, color: Theme.colors.mediumGrey, marginRight: 10 }} />
                                </Tooltip>

                                <Text small bold>
                                    {
                                        usage.isFamilyCombinedMaximum ?
                                            i18n.t(tokens.labels.coverage.combinedFamilyMaximumLabel, { combinedMax: numberWithCommas(usage.combinedMax.toFixed(2)) })
                                            :
                                            i18n.t(tokens.labels.coverage.combinedMaximumCombinedmax, { combinedMax: numberWithCommas(usage.combinedMax.toFixed(2)) })
                                    }
                                </Text>
                            </div>

                            <Text small bold margin="0 0 10px 0" align="right">
                                { i18n.t(tokens.labels.amountRemaining, { amount: calculateCombinedParamedicalsTotals().paramedical }) }
                            </Text>
                        </div>
                }
            </div>

            {
                usage.used.map((item) => {
                    if (coverage[item.coverageColumn]) {
                        if (usage.isMentalWellnessIncluded && [ 'Social Worker', 'Psychologist', 'Psychotherapist' ].indexOf(item.type) !== -1) {
                            return null;
                        }

                        if (usage.isFunctionalTreatmentIncluded && [ 'Osteopath', 'Chiropodist', 'Podiatrist' ].indexOf(item.type) !== -1) {
                            return null;
                        }

                        return (
                            <UsageBlock
                                key={ item.type }
                                usage={{
                                    type: item.type,
                                    limit: coverage[item.coverageColumn],
                                    used: item.used
                                }}
                                fillColor={ fillColor }
                                showRemaining={ !isCombined }
                            />
                        );
                    }

                    return null;
                })
            }

            {
                usage.unused.map((item) => {
                    if (coverage[item.coverageColumn]) {
                        if (usage.isMentalWellnessIncluded && [ 'Social Worker', 'Psychologist', 'Psychotherapist' ].indexOf(item.type) !== -1) {
                            return null;
                        }

                        if (usage.isFunctionalTreatmentIncluded && [ 'Osteopath', 'Chiropodist', 'Podiatrist' ].indexOf(item.type) !== -1) {
                            return null;
                        }

                        return (
                            <UsageBlock
                                key={ item.type }
                                usage={{
                                    type: item.type,
                                    limit: coverage[item.coverageColumn],
                                    used: item.used
                                }}
                                fillColor={ fillColor }
                            />
                        );
                    }

                    return null;
                })
            }

            {
                usage.isMentalWellnessIncluded &&
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                            <Text
                                medium
                                bold
                                customStyle={{
                                    flex: 1,
                                    display: 'inline',
                                    margin: '0 0 10px 0'
                                }}
                            >
                                { i18n.t(tokens.labels.coverage.mentalWellnessLabel) }
                            </Text>

                            {
                                waivedEHC &&
                                    <Chip
                                        size="small"
                                        label="Waived"
                                        color="primary"
                                        variant="outlined"
                                        style={{
                                            margin: "5px 10px",
                                            borderRadius: 30,
                                            borderWidth: 2,
                                            borderColor: Theme.colors.lightGrey,
                                            fontFamily: Theme.fonts.type.heading,
                                            fontSize: 14,
                                            color: '#666'
                                        }}
                                    />
                            }

                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                    <Tooltip
                                        title={ i18n.t(tokens.labels.coverage.spendUpToAmountOnMentalWellnessServices, { amount: calculateCombinedParamedicalsTotals().mentalWellness })}
                                        arrow
                                        placement="top"
                                    >
                                        <InfoIcon aria-label="info" style={{ fontSize: 15, color: Theme.colors.mediumGrey, marginRight: 10 }} />
                                    </Tooltip>

                                    <Text small bold>
                                        { i18n.t(tokens.labels.coverage.combinedMaximumCombinedmax, { combinedMax: numberWithCommas(usage.mentalWellnessCombinedMax.toFixed(2)) })}
                                    </Text>
                                </div>

                                <Text small bold margin="0 0 10px 0" align="right">
                                    { i18n.t(tokens.labels.amountRemaining, { amount: calculateCombinedParamedicalsTotals().mentalWellness }) }
                                </Text>
                            </div>
                        </div>

                        {
                            usage.used.map((item) => {
                                if (
                                    coverage[item.coverageColumn] &&
                                    (item.type === 'Social Worker' || item.type === 'Psychologist' || item.type === 'Psychotherapist')
                                ) {
                                    return (
                                        <UsageBlock
                                            key={ item.type }
                                            usage={{
                                                type: item.type,
                                                limit: coverage[item.coverageColumn],
                                                used: item.used
                                            }}
                                            fillColor={ fillColor }
                                            showRemaining={ !usage.isMentalWellnessIncluded }
                                        />
                                    );
                                }

                                return null;
                            })
                        }

                        {
                            usage.unused.map((item) => {
                                if (
                                    coverage[item.coverageColumn] &&
                                    (item.type === 'Social Worker' || item.type === 'Psychologist' || item.type === 'Psychotherapist')
                                ) {
                                    return (
                                        <UsageBlock
                                            key={ item.type }
                                            usage={{
                                                type: item.type,
                                                limit: coverage[item.coverageColumn],
                                                used: item.used
                                            }}
                                            fillColor={ fillColor }
                                        />
                                    );
                                }

                                return null;
                            })
                        }
                    </>
            }

            {
                usage.isFunctionalTreatmentIncluded &&
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                            <Text
                                medium
                                bold
                                customStyle={{
                                    flex: 1,
                                    display: 'inline',
                                    margin: '0 0 10px 0'
                                }}
                            >
                                { i18n.t(tokens.labels.coverage.functionalTreatmentLabel) }
                            </Text>

                            {
                                waivedEHC &&
                                <Chip
                                    size="small"
                                    label="Waived"
                                    color="primary"
                                    variant="outlined"
                                    style={{
                                        margin: "5px 10px",
                                        borderRadius: 30,
                                        borderWidth: 2,
                                        borderColor: Theme.colors.lightGrey,
                                        fontFamily: Theme.fonts.type.heading,
                                        fontSize: 14,
                                        color: '#666'
                                    }}
                                />
                            }

                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                    <Tooltip
                                        title={ i18n.t(tokens.labels.coverage.spendUpToAmountOnFunctionalTreatmentServices, { amount: calculateCombinedParamedicalsTotals().functionalTreatment })}
                                        arrow
                                        placement="top"
                                    >
                                        <InfoIcon aria-label="info" style={{ fontSize: 15, color: Theme.colors.mediumGrey, marginRight: 10 }} />
                                    </Tooltip>

                                    <Text small bold>
                                        { i18n.t(tokens.labels.coverage.combinedMaximumCombinedmax, { combinedMax: numberWithCommas(usage.functionalTreatmentCombinedMax.toFixed(2)) })}
                                    </Text>
                                </div>

                                <Text small bold margin="0 0 10px 0" align="right">
                                    { i18n.t(tokens.labels.amountRemaining, { amount: calculateCombinedParamedicalsTotals().functionalTreatment }) }
                                </Text>
                            </div>
                        </div>

                        {
                            usage.used.map((item) => {
                                if (
                                    coverage[item.coverageColumn] &&
                                    (item.type === 'Osteopath' || item.type === 'Chiropodist' || item.type === 'Podiatrist')
                                ) {
                                    return (
                                        <UsageBlock
                                            key={ item.type }
                                            usage={{
                                                type: item.type,
                                                limit: coverage[item.coverageColumn],
                                                used: item.used
                                            }}
                                            fillColor={ fillColor }
                                            showRemaining={ !usage.isMentalWellnessIncluded }
                                        />
                                    );
                                }

                                return null;
                            })
                        }

                        {
                            usage.unused.map((item) => {
                                if (
                                    coverage[item.coverageColumn] &&
                                    (item.type === 'Osteopath' || item.type === 'Chiropodist' || item.type === 'Podiatrist')
                                ) {
                                    return (
                                        <UsageBlock
                                            key={ item.type }
                                            usage={{
                                                type: item.type,
                                                limit: coverage[item.coverageColumn],
                                                used: item.used
                                            }}
                                            fillColor={ fillColor }
                                        />
                                    );
                                }

                                return null;
                            })
                        }
                    </>
            }
        </React.Fragment>
    );
};

export default ParamedicalsUsageBlock;